import React from "react";
import { useMutation } from "@apollo/client";
import {
  LEARNER_SCHEDULE_ON_DEMAND_CALENDAR,
  LEARNER_DELETE_ON_DEMAND_CALENDAR,
} from "utility/graphQl/mutation";

import appInfo from "utility/app-info";
import { LearnerRecording } from "../api/types";

import { logError } from "utility/Function";
import { trackEvent } from "utility/mixpanel";
import { useToast } from "components/ui";
import { ReactComponent as Checkmark } from "Assets/icon/check-mark.svg";
import styles from "../pages/recordings/recording-detail/index.module.scss";

const createErrorMessage = (action: string) =>
  `Could not ${action}. Please try again. If the problem persists, contact support@walkingonearth.com`;

const TOAST_MESSAGES = {
  SCHEDULE_SUCCESS: {
    type: "success",
    message: "Session successfully added to your calendar",
  },
  DELETE_SCHEDULE_SUCCESS: {
    type: "success",
    message: "Session successfully removed from your calendar",
  },
  SCHEDULE_ERROR: {
    type: "error",
    messageHeader: "Scheduling failed",
    message: createErrorMessage("schedule this session"),
  },
  DELETE_SCHEDULE_ERROR: {
    type: "error",
    messageHeader: "Deletion failed",
    message: createErrorMessage("delete your scheduled session"),
  },
} as const;

const RECORDING_TYPENAME = "LearnerRecording";

interface Props {
  recording: LearnerRecording;
}

export const useRecordingSessionActions = ({ recording }: Props) => {
  const { openToast } = useToast();

  const showToast = (messageType: keyof typeof TOAST_MESSAGES) => {
    const message = TOAST_MESSAGES[messageType];
    openToast({
      messageHeader:
        "messageHeader" in message ? message.messageHeader : undefined,
      message: message.message,
      size: "medium",
      ...(message.type === "success" && {
        icon: <Checkmark className={styles["check-mark-icon"]} />,
      }),
    });
  };

  const [scheduleOnDemandCalendar, { loading: isScheduling }] = useMutation(
    LEARNER_SCHEDULE_ON_DEMAND_CALENDAR,
    {
      onCompleted: () => {
        showToast("SCHEDULE_SUCCESS");
      },
      onError: (error) => {
        showToast("SCHEDULE_ERROR");
        logError("Error scheduling on-demand calendar session", error);
      },
    }
  );

  const [deleteOnDemandCalendar, { loading: isDeleting }] = useMutation(
    LEARNER_DELETE_ON_DEMAND_CALENDAR,
    {
      variables: {
        recordingId: recording?.id,
        appInfo,
      },
      onCompleted: () => {
        showToast("DELETE_SCHEDULE_SUCCESS");
      },
      onError: (error) => {
        showToast("DELETE_SCHEDULE_ERROR");
        logError("Error deleting on-demand calendar session", error);
      },
    }
  );

  const handleScheduleOnDemandCalendar = (datetimeSlotUtc: string) => {
    if (recording?.__typename !== RECORDING_TYPENAME) {
      throw new Error(
        `Invalid recording type "${recording?.__typename}". Expected "${RECORDING_TYPENAME}"`
      );
    }

    trackEvent("Recording Schedule Calendar Clicked", {
      "Recording ID": recording?.id,
      "Recording Title": recording?.title,
      "Datetime Slot UTC": datetimeSlotUtc,
    });

    scheduleOnDemandCalendar({
      variables: {
        recordingId: recording?.id,
        datetimeSlotUtc,
        appInfo,
      },
    });
  };

  const handleDeleteOnDemandCalendar = () => {
    if (recording?.__typename !== RECORDING_TYPENAME) {
      throw new Error(
        `Invalid recording type "${recording?.__typename}". Expected "${RECORDING_TYPENAME}"`
      );
    }

    trackEvent("Recording Delete Calendar Clicked", {
      "Recording ID": recording?.id,
      "Recording Title": recording?.title,
    });

    deleteOnDemandCalendar();
  };

  const actions = {
    SCHEDULE: {
      loading: isScheduling,
      action: handleScheduleOnDemandCalendar,
    },
    DELETE: {
      loading: isDeleting,
      action: handleDeleteOnDemandCalendar,
    },
  };

  return actions;
};
